import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@vigia/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only SERIALIZES the model the screen already renders; the
 * format lives in `buildDiagnosticsReport` (shared, pure and tested function).
 * It receives the SAME array of sections that is painted, so adding a row
 * above makes it appear in the report without touching anything here. It used to be two
 * sources —the screen on one side and its own object on the other— and the second
 * fell behind silently.
 *
 * Labels: `Copy` and `Copied` are used, which are already translated in the eleven
 * languages. A new string like "Copy diagnostics" would come out in ENGLISH in all
 * of them without the i18n gate complaining, because it only validates what is already in
 * the catalogue.
 *
 * Here it is `Copied` and not `Copied!` —as in the rest of the apps— because this
 * repo does NOT have `Copied!` in the catalogue: it is the form the CLI
 * creation dialog already uses. With the other one, the button would say "Copied!" in English in the eleven
 * languages and the gate would say nothing.
 */
/** How long the "Copied!" is kept before going back to the normal label. */
/** How long the "Copied!" is kept before going back to the normal label. */
const CONFIRMATION_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model the screen renders. */
  sections: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copy = async () => {
    const text = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.sections(),
    });
    try {
      await navigator.clipboard.writeText(text);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Silencing it
      // would leave the user believing they copied: the failure is flagged and the
      // report is left selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copied() ? t(msg`Copied`) : t(msg`Copy`)}
      </Button>

      {/* Plan B when clipboard is unavailable: visible report for manual selection */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.sections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
